import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { IRequestCallbackFunction, IRequestSubjectEvent, RequestService } from '@df/api/request/request.service';
import { Status } from '@df/core/status';
import { NBarclaycardCybersource } from '@df/module-barclaycard/barclaycard-cybersource.namespace';
import { TimeoutService } from '@df/ng/timeout.service';
import { Nexus } from '@tomandco/nexus-api-types';

@Injectable({ providedIn: 'root' })
export class BarclaycardCybersourceService {
    private renderer: Renderer2;

    constructor(private timeoutService: TimeoutService, private requestService: RequestService, private rendererFactory: RendererFactory2) {
        this.renderer = this.rendererFactory.createRenderer(null, null);
    }

    initCardinal(isTest: boolean) {
        if (isTest) {
            this.cardinalInstance?.configure({ logging: { level: 'on' } });
        }
    }

    setupCardinal(
        checkoutSubmissionData: NBarclaycardCybersource.ISubmissionPaymentData
    ): Promise<NBarclaycardCybersource.ISetupCardinalResponse> {
        return new Promise((resolve, reject) => {
            const cardinalInstance = this.cardinalInstance;
            if (cardinalInstance) {
                this.getCardinalToken(checkoutSubmissionData).then(token => {
                    cardinalInstance.on(
                        NBarclaycardCybersource.ECardinalEventName.setupComplete,
                        (result: NBarclaycardCybersource.ICardinalSetupCompleteResult) => {
                            cardinalInstance.off(NBarclaycardCybersource.ECardinalEventName.setupComplete);
                            this.timeoutService.setTimeout(() => {
                                resolve({ sessionId: result?.sessionId, token });
                            }, 1);
                        }
                    );
                    cardinalInstance.setup('init', { jwt: token });
                }, reject);
            } else {
                reject('Cardinal instance not found');
            }
        });
    }

    binDetect(cardNumber: string) {
        return new Promise((resolve, reject) => {
            const cardinalInstance = this.cardinalInstance;
            if (cardinalInstance) {
                cardinalInstance.trigger('bin.process', cardNumber).then(resolve).catch(reject);
            } else {
                reject('Cardinal instance not found');
            }
        });
    }

    jwtDecode(token: string): NBarclaycardCybersource.ICardinalJwt {
        try {
            const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(
                atob(base64)
                    .split('')
                    .map(function (c) {
                        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
                    })
                    .join('')
            );
            return JSON.parse(jsonPayload);
        } catch (e) {
            return undefined;
        }
    }

    checkAuthentication(
        status: Status,
        submitStatus: Status,
        onSuccess?: IRequestCallbackFunction,
        onError?: IRequestCallbackFunction
    ): void {
        const requestSubscription = this.requestService.requestSubject.subscribe((request: IRequestSubjectEvent) => {
            if (request.status === 'error' && request.path === Nexus.Api.Checkout.Submit.path && submitStatus.promise?.cancel) {
                requestSubscription.unsubscribe();
                const response = request?.responseData as NBarclaycardCybersource.ICheckoutSubmitResponse;
                const code = response?.code;
                if ((code === 475 && response.cca && response.order) || code === 478) {
                    submitStatus.promise.cancel();
                    status.busy = true;
                    if (code === 478) {
                        onSuccess('');
                    } else {
                        this.continueCardinal(response.cca, response.order).then(onSuccess).catch(onError);
                    }
                }
            }
        });
    }

    initDeviceDataCollection(token: string, isTest: boolean, container: Node): void {
        const action = isTest
            ? NBarclaycardCybersource.EDeviceDataCollectionEndpoints.staging
            : NBarclaycardCybersource.EDeviceDataCollectionEndpoints.production;

        const target = 'collectionIframe';

        const form = this.renderer.createElement('form');
        this.renderer.setAttribute(form, 'method', 'post');
        this.renderer.setAttribute(form, 'action', action);
        this.renderer.addClass(form, 'ng-hide');
        const hiddenField = this.renderer.createElement('input');
        this.renderer.setAttribute(hiddenField, 'type', 'hidden');
        this.renderer.setAttribute(hiddenField, 'name', 'JWT');
        this.renderer.setAttribute(hiddenField, 'value', token);
        form.appendChild(hiddenField);

        this.renderer.appendChild(container, form);
        this.renderer.setAttribute(form, 'target', target);

        const iframe = this.renderer.createElement('iframe');
        this.renderer.setAttribute(iframe, 'name', target);
        this.renderer.addClass(iframe, 'ng-hide');
        form.parentNode.insertBefore(iframe, form);
        form.submit();
    }

    private continueCardinal(cca: NBarclaycardCybersource.ICca, order: NBarclaycardCybersource.IOrder): Promise<string> {
        return new Promise((resolve, reject) => {
            const cardinalInstance = this.cardinalInstance;
            if (cardinalInstance) {
                cardinalInstance.on(
                    NBarclaycardCybersource.ECardinalEventName.validated,
                    (data: NBarclaycardCybersource.ICardinalPaymentValidatedData, jwt: string) => {
                        cardinalInstance.off(NBarclaycardCybersource.ECardinalEventName.validated);
                        const errorNumber = data?.ErrorNumber;
                        if (errorNumber !== 0) {
                            reject(data?.ErrorDescription);
                        } else {
                            resolve(jwt);
                        }
                    }
                );

                cardinalInstance.continue('cca', cca, order);
            } else {
                reject('Cardinal instance not found');
            }
        });
    }

    private getCardinalToken(checkoutSubmissionData: NBarclaycardCybersource.ISubmissionPaymentData): Promise<string> {
        return new Promise((resolve, reject) => {
            this.requestService.send(
                undefined,
                { path: NBarclaycardCybersource.EEndpoints.CreateThreeDSecureToken, data: checkoutSubmissionData },
                (response: NBarclaycardCybersource.ICardinalGetTokenResponse) => {
                    if (!response?.success) {
                        reject(response.error_msg || NBarclaycardCybersource.AUTH_ERROR_MESSAGE);
                    }
                    resolve(response.accessToken);
                },
                reject
            );
        });
    }

    private get cardinalInstance(): NBarclaycardCybersource.ICardinalInstance | undefined {
        if (typeof window !== 'undefined') {
            return window.Cardinal;
        }
        return undefined;
    }
}
